import {
  IClientSettings,
  IClientSettingsCalculatedPositions,
  IClientSettingsCentrix,
  IClientSettingsCommonUX,
  IClientSettingsCompanyInfo,
  IClientSettingsDepartment,
  IClientSettingsEmail,
  IClientSettingsEmoRisk,
  IClientSettingsEnumItem,
  IClientSettingsEnums,
  IClientSettingsFilter,
  IClientSettingsImpact,
  IClientSettingsOrganization,
  IClientSettingsPosition,
  IClientSettingsPositionGroup,
  IClientSettingsRiskHR,
  ICompanyInfo,
  IRolesAndPermissions,
  ISeverityTopicTRF,
  IUser,
} from '@logicalcommander/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PersonDisplayMap } from '../../ui/components/lc-admin-name/person-display'
import { RootState } from '../store'

export interface IAssessmentSizes {
  [sizeId: number]: IAssessmentTypes
}

export interface IAssessmentTypes {
  [typeId: number]: IAssessmentTypeItem[]
}

export interface ITRFLevels {
  [id: number]: number
}

export interface IAssessmentTypeItem {
  nameId: number
  customName?: string
  assessmentId: number
}

export interface ISettingsState extends IClientSettings {
  assessmentsSizes: IAssessmentSizes
  roles: IRolesAndPermissions[]
  admins: IUser[]
  personDisplayNames: PersonDisplayMap
}

const initialState: ISettingsState = {
  emails: {} as IClientSettingsEmail,
  emoRisk: {} as IClientSettingsEmoRisk,
  centrix: {} as IClientSettingsCentrix,
  riskHR: {} as IClientSettingsRiskHR,
  commonUX: {} as IClientSettingsCommonUX,
  enums: {} as IClientSettingsEnums,
  companyInfo: {} as IClientSettingsCompanyInfo,
  filters: [] as IClientSettingsFilter[],
  assessmentsSizes: [] as IAssessmentSizes,
  roles: [] as IRolesAndPermissions[],
  admins: [] as IUser[],
  personDisplayNames: {} as PersonDisplayMap,
}

const debug = false

export const settingsSlice = createSlice({
  name: 'settingsReducer',
  initialState,
  reducers: {
    setEmailsSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsEmail>) => {
      if (debug) console.log('settingsSlice:: setEmailsSettings: action.payload:', action.payload)
      const newState = { ...state }
      newState.emails = action.payload
      if (debug) console.log('settingsSlice:: setEmailsSettings: state after update:', newState)
      return newState
    },
    setUXSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsCommonUX>) => {
      if (debug) console.log('settingsSlice:: setUXSettings: action.payload:', action.payload)
      const newState = { ...state }
      newState.commonUX = action.payload
      if (debug) console.log('settingsSlice:: setUXSettings: state after update:', newState)
      return newState
    },
    setCompanyInfoSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsCompanyInfo>) => {
      if (debug) console.log('settingsSlice:: setCompanySettings: action.payload:', action.payload)
      const newState = { ...state }
      newState.companyInfo = action.payload
      if (debug) console.log('settingsSlice:: setCompanySettings: state after update:', newState)
      return newState
    },
    setDepartmentsSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsDepartment[]>) => {
      if (debug) console.log('settingsSlice:: setDepartmentsSettings: action.payload:', action.payload)
      state.enums.departments = action.payload
      if (debug) console.log('settingsSlice:: setDepartmentsSettings: state after update:', state)
      return state
    },
    setPositionsSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsPosition[]>) => {
      if (debug) console.log('settingsSlice:: setPositionsSettings: action.payload:', action.payload)
      state.enums.positions = action.payload
      if (debug) console.log('settingsSlice:: setPositionsSettings: state after update:', state)
      return state
    },
    setOrganizationsSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsOrganization[]>) => {
      if (debug) console.log('settingsSlice:: setOrganizationsSettings: action.payload:', action.payload)
      state.enums.organizations = action.payload
      if (debug) console.log('settingsSlice:: setOrganizationsSettings: state after update:', state)
      return state
    },
    setEmployeeStatusSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsEnumItem[]>) => {
      if (debug) console.log('settingsSlice:: setEmployeeStatusSettings: action.payload:', action.payload)
      state.enums.employeeStatus = action.payload
      if (debug) console.log('settingsSlice:: setEmployeeStatusSettings: state after update:', state)
      return state
    },
    setAssessmentsSizesSettings: (state: ISettingsState, action: PayloadAction<IAssessmentSizes>) => {
      if (debug) console.log('settingsSlice:: setAssessmentsSizesSettings: action.payload:', action.payload)
      state.assessmentsSizes = action.payload
      if (debug) console.log('settingsSlice:: setAssessmentsSizesSettings: state after update:', state)
      return state
    },
    setRolesSettings: (state: ISettingsState, action: PayloadAction<IRolesAndPermissions[]>) => {
      if (debug) console.log('settingsSlice:: setRolesSettings: action.payload:', action.payload)
      state.roles = action.payload
      if (debug) console.log('settingsSlice:: setRolesSettings: state after update:', state)
      return state
    },
    setAdminsSettings: (state: ISettingsState, action: PayloadAction<IUser[]>) => {
      if (debug) console.log('settingsSlice:: setAdminsSettings: action.payload:', action.payload)
      state.admins = action.payload
      if (debug) console.log('settingsSlice:: setAdminsSettings: state after update:', state)
      return state
    },
    mergePersonDisplayNames: (
      state: ISettingsState,
      action: PayloadAction<{ requested: string[]; rows: { identity: string; fullName: string; inactive: boolean }[] }>
    ) => {
      const found = new Set(action.payload.rows.map((row) => row.identity))
      action.payload.rows.forEach((row) => {
        state.personDisplayNames[row.identity] = { fullName: row.fullName, inactive: row.inactive }
      })
      action.payload.requested.forEach((identity) => {
        if (!found.has(identity)) {
          state.personDisplayNames[identity] = { fullName: '', inactive: true, unknown: true }
        }
      })
    },
    setRiskHrSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsRiskHR>) => {
      if (debug) console.log('settingsSlice:: setRiskHrSettings: action.payload:', action.payload)
      const newState = { ...state }
      newState.riskHR = action.payload
      if (debug) console.log('settingsSlice:: setRiskHrSettings: state after update:', newState)
      return newState
    },
    setPositionsGroupsSettings: (state: ISettingsState, action: PayloadAction<IClientSettingsPositionGroup[]>) => {
      if (debug) console.log('settingsSlice:: setPositionsGroupsSettings: action.payload:', action.payload)
      state.enums.positionsGroups = action.payload
      if (debug) console.log('settingsSlice:: setPositionsGroupsSettings: state after update:', state)
      return state
    },
    setAllSettings: (state: ISettingsState, action: PayloadAction<IClientSettings>) => {
      if (debug) console.log('settingsSlice:: setSettings: action.payload:', action.payload)
      const newState = {
        ...state,
        emails: action.payload.emails,
        emoRisk: action.payload.emoRisk,
        riskHR: action.payload.riskHR,
        centrix: action.payload.centrix,
        commonUX: action.payload.commonUX,
        enums: action.payload.enums,
        companyInfo: action.payload.companyInfo,
      }
      if (debug) console.log('settingsSlice:: state after update:', newState)
      return newState
    },
  },
})

export const {
  setAllSettings,
  setEmailsSettings,
  setUXSettings,
  setCompanyInfoSettings,
  setDepartmentsSettings,
  setPositionsSettings,
  setOrganizationsSettings,
  setEmployeeStatusSettings,
  setAssessmentsSizesSettings,
  setRolesSettings,
  setAdminsSettings,
  mergePersonDisplayNames,
  setRiskHrSettings,
  setPositionsGroupsSettings,
} = settingsSlice.actions
export default settingsSlice.reducer
export const selectSettings = (state: RootState): ISettingsState => state.settingsReducer
export const selectSettingsCommonUX = (state: RootState): IClientSettingsCommonUX => state.settingsReducer.commonUX

// Stable empty arrays so selectors return same reference when value is nullish (avoids unnecessary re-renders from useSelector)
const EMPTY_POSITIONS: IClientSettingsPosition[] = []
const EMPTY_POSITIONS_GROUPS: IClientSettingsPositionGroup[] = []
const EMPTY_CALCULATED_SEVERITY: IClientSettingsCalculatedPositions[] = []
const EMPTY_IMPACTS: IClientSettingsImpact[] = []
const EMPTY_DEPARTMENTS: IClientSettingsDepartment[] = []
const EMPTY_EMPLOYEE_STATUS: IClientSettingsEnumItem[] = []
const EMPTY_ORGANIZATIONS: IClientSettingsOrganization[] = []
const EMPTY_ROLES: IRolesAndPermissions[] = []
const EMPTY_ADMINS: IUser[] = []
const EMPTY_PERSON_DISPLAY_NAMES: PersonDisplayMap = {}
const EMPTY_IMPACT_SDG_MAP: Record<string, string[]> = {}
const EMPTY_SDG_META: Record<string, { number: number; color: string }> = {}

export const selectPositions = (state: RootState): IClientSettingsPosition[] => state.settingsReducer.enums.positions ?? EMPTY_POSITIONS
export const selectPositionsGroups = (state: RootState): IClientSettingsPositionGroup[] =>
  state.settingsReducer.enums.positionsGroups ?? EMPTY_POSITIONS_GROUPS
export const selectCalculatedPositionsSeverity = (state: RootState): IClientSettingsCalculatedPositions[] =>
  state.settingsReducer.riskHR.severityPositionsCalculated ?? EMPTY_CALCULATED_SEVERITY
export const getImpacts = (state: RootState): IClientSettingsImpact[] => state.settingsReducer.riskHR.impacts ?? EMPTY_IMPACTS

/** AppConfig SDG taxonomy stamped on GET /settings (not company-persisted). */
export const getImpactSdgMap = (state: RootState): Record<string, string[]> =>
  ((state.settingsReducer.riskHR as { impactSdgMap?: Record<string, string[]> }).impactSdgMap) ?? EMPTY_IMPACT_SDG_MAP

export const getSdgMeta = (state: RootState): Record<string, { number: number; color: string }> =>
  ((state.settingsReducer.riskHR as { sdgMeta?: Record<string, { number: number; color: string }> }).sdgMeta) ?? EMPTY_SDG_META
export const selectDepartments = (state: RootState): IClientSettingsDepartment[] =>
  state.settingsReducer.enums.departments ?? EMPTY_DEPARTMENTS
export const selectEmployeeStatus = (state: RootState): IClientSettingsEnumItem[] =>
  state.settingsReducer.enums.employeeStatus ?? EMPTY_EMPLOYEE_STATUS
export const selectOrganizations = (state: RootState): IClientSettingsOrganization[] =>
  state.settingsReducer.enums.organizations ?? EMPTY_ORGANIZATIONS
export const selectAllRoles = (state: RootState): IRolesAndPermissions[] => state.settingsReducer.roles ?? EMPTY_ROLES
export const selectAllAdmins = (state: RootState): IUser[] => state.settingsReducer.admins ?? EMPTY_ADMINS
export const selectPersonDisplayNames = (state: RootState): PersonDisplayMap =>
  state.settingsReducer.personDisplayNames ?? EMPTY_PERSON_DISPLAY_NAMES
export const selectCompanyInfo = (state: RootState): ICompanyInfo => state.settingsReducer.companyInfo
export const selectTRFLevels = (state: RootState): { id: number; topicsTRF: ISeverityTopicTRF }[] => {
  const groups = state.settingsReducer.riskHR?.severityPositionsGroups
  if (!groups?.length) return []
  return groups.map((group) => ({
    id: group.id,
    topicsTRF: group.topicsTRF,
  }))
}
